import { createSelector } from '@reduxjs/toolkit'
import { createStoryPathSlice } from './storyPath/createStoryPathSlice.js'
import scientificDetectiveStories from './scientific_detective/stories.js'
import nefariousHeroStories from './nefarious_hero/stories.js'
import networkersStories from './networkers/stories.js'
import factoryStories from './factory/stories.js'
import { selectIsOpeningSceneComplete } from './opening_scene/openingSceneSlice.js'

// The single source of truth for the four parallel story paths. Order here is
// the render order for scenes/markers/conversations.
// tooltipDirection is which side of the icon the marker's label sits on. Each
// path uses a different side so labels of nearby markers don't stack on top of
// each other.
function makePath ({ key, label, icon, tooltipDirection, hasConversation = true, scenesThatGoStraightToNextScene = [], stories }) {
  return {
    key,
    label,
    icon,
    tooltipDirection,
    hasConversation,
    slice: createStoryPathSlice({ name: key, stories, hasConversation, scenesThatGoStraightToNextScene })
  }
}

export const storyPaths = [
  makePath({ key: 'scientificDetective', tooltipDirection: 'top', label: 'Sci Det', icon: 'cat.svg', scenesThatGoStraightToNextScene: [3], stories: scientificDetectiveStories }),
  makePath({ key: 'nefariousHero', tooltipDirection: 'bottom', label: 'Nef Hero', icon: 'handcuffs.svg', scenesThatGoStraightToNextScene: [], stories: nefariousHeroStories }),
  makePath({ key: 'networkers', tooltipDirection: 'left', label: 'Networkers', icon: 'networkers.svg', scenesThatGoStraightToNextScene: [], stories: networkersStories }),
  makePath({ key: 'factory', tooltipDirection: 'right', label: 'Factory', icon: 'factory.svg', hasConversation: false, scenesThatGoStraightToNextScene: [], stories: factoryStories })
]

export const pathsByKey = Object.fromEntries(storyPaths.map((path) => [path.key, path]))

// The currently-visible map markers across every path, in one memoized array.
// Each path's selectMarker returns a stable reference (a marker object from the
// static stories data, or false), so this only recomputes when a marker
// actually appears/disappears — not on every 1s timerTick.
export const selectActiveMapMarkers = createSelector(
  storyPaths.map((path) => path.slice.selectors.selectMarker),
  (...markers) =>
    markers
      .map((marker, i) =>
        marker && {
          key: storyPaths[i].key,
          icon: storyPaths[i].icon,
          location: marker.location,
          text: marker.text
        }
      )
      .filter(Boolean)
)

// True once there's nothing left for the player to do: the opening scene is
// finished, and no path has a marker, conversation, or open scene visible.
// Drives the free-play "nothing available in this time slot" teaser - the
// free-play equivalent of timescale mode's cooldown wait.
export const selectIsNothingAvailableInFreePlay = createSelector(
  [
    selectIsOpeningSceneComplete,
    ...storyPaths.map((path) => path.slice.selectors.selectMarker),
    ...storyPaths.map((path) => path.slice.selectors.selectIsShowConversation),
    ...storyPaths.map((path) => path.slice.selectors.selectIsShowScene)
  ],
  (isOpeningSceneComplete, ...visibilityFlags) =>
    isOpeningSceneComplete && visibilityFlags.every((flag) => !flag)
)
